import React, { useState } from 'react';
import { Box, Typography, Container, CircularProgress, Alert, Fade } from '@mui/material';
import { useDashboard } from '../hooks/useDashboard';
import DeliveryFilter from '../components/deliveryAnalytics/DeliveryFilter';
import DeliveryOverview from '../components/deliveryAnalytics/DeliveryOverview';
import DeliveryTrendChart from '../components/deliveryAnalytics/DeliveryTrendChart';
import DeliveryPerformance from '../components/deliveryAnalytics/DeliveryPerformance';
import DeliveryPersonnel from '../components/deliveryAnalytics/DeliveryPersonnel';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTruck } from '@fortawesome/free-solid-svg-icons';
import { useWarehouseContext } from '../context/WarehouseContext';
import { useTranslation } from 'react-i18next';

const DeliveryAnalytics = () => {
    const { t } = useTranslation();
    const {
        getDeliveryChallans,
        getDeliveryPerformance,
        getDeliveryPersonnel
    } = useDashboard();

    const [period, setPeriod] = useState('last_30_days');

    const [dateRange, setDateRange] = useState(() => {
        const today = new Date();
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        return {
            from_date: startOfMonth.toISOString().split('T')[0],
            to_date: endOfMonth.toISOString().split('T')[0],
        };
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { selectedWarehouseIds } = useWarehouseContext();

    // State for all data sections
    const [data, setData] = useState({
        challans: null,
        performance: null,
        personnel: null
    });

    const handleDateChange = (field, value) => {
        setDateRange(prev => ({ ...prev, [field]: value }));
    };

    const handlePeriodChange = (value) => {
        setPeriod(value);
    };

    // Ref to track if a fetch is in progress
    const fetchingRef = React.useRef(false);

    const fetchData = React.useCallback(async (force = false) => {
        // For custom period, require dates
        if (period === 'custom' && (!dateRange.from_date || !dateRange.to_date)) return;

        // Prevent double fetch if already fetching
        if (fetchingRef.current) return;

        const warehouseKey = selectedWarehouseIds?.length > 0 ? `_w${selectedWarehouseIds.join('_')}` : '';
        const cacheKey = period === 'custom'
            ? `deliveryAnalytics_custom_${dateRange.from_date}_${dateRange.to_date}${warehouseKey}`
            : `deliveryAnalytics_${period}${warehouseKey}`;
        const cachedData = localStorage.getItem(cacheKey);

        if (!force && cachedData) {
            try {
                const parsed = JSON.parse(cachedData);
                if (parsed) {
                    setData(parsed);
                    return;
                }
            } catch (e) {
                console.error("Failed to parse cached data", e);
                localStorage.removeItem(cacheKey);
            }
        }

        fetchingRef.current = true;
        setLoading(true);
        setError(null);

        try {
            const params = { period };

            // Only include dates for custom period
            if (period === 'custom') {
                params.from_date = dateRange.from_date;
                params.to_date = dateRange.to_date;
            }

            // Execute all requests in parallel
            const [
                challansRes,
                performanceRes,
                personnelRes
            ] = await Promise.all([
                getDeliveryChallans(params),
                getDeliveryPerformance(params),
                getDeliveryPersonnel(params)
            ]);

            // Check for validation/errors in responses
            const newError = challansRes.error || performanceRes.error || personnelRes.error;

            if (newError) {
                setError(newError);
            }

            const newData = {
                challans: challansRes.data || null,
                performance: performanceRes.data || null,
                personnel: personnelRes.data || null
            };

            setData(newData);
            localStorage.setItem(cacheKey, JSON.stringify(newData));

        } catch (err) {
            setError(err.message || t('hookErrors.analyticsPage.fetchFailed'));
            console.error(err);
        } finally {
            setLoading(false);
            fetchingRef.current = false;
        }
    }, [period, dateRange, getDeliveryChallans, getDeliveryPerformance, getDeliveryPersonnel, selectedWarehouseIds]);

    // Initial fetch on mount
    React.useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedWarehouseIds]); // Run once on mount or when warehouse selection changes

    const strings = t('deliveryAnalytics.page', { returnObjects: true });

    return (
        <Container maxWidth={false} sx={{ py: 4 }}>
            <Box mb={5}>
                <Box display="flex" alignItems="center" gap={2} mb={1}>
                    <Box
                        sx={{
                            bgcolor: 'primary.main',
                            borderRadius: 2,
                            width: 40,
                            height: 40,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white'
                        }}
                    >
                        <FontAwesomeIcon icon={faTruck} />
                    </Box>
                    <Typography variant="h4" fontWeight="800" letterSpacing="-0.5px">
                        {strings.title}
                    </Typography>
                </Box>
                <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 800 }}>
                    {strings.subtitle}
                </Typography>
            </Box>

            <DeliveryFilter
                fromDate={dateRange.from_date}
                toDate={dateRange.to_date}
                onFromDateChange={(val) => handleDateChange('from_date', val)}
                onToDateChange={(val) => handleDateChange('to_date', val)}
                onSubmit={() => fetchData(true)}
                loading={loading}
                period={period}
                onPeriodChange={handlePeriodChange}
            />

            {error && (
                <Fade in>
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {typeof error === 'object' ? JSON.stringify(error) : error}
                    </Alert>
                </Fade>
            )}

            {loading && !data.challans && (
                <Box display="flex" justifyContent="center" p={10}>
                    <CircularProgress size={40} thickness={4} />
                </Box>
            )}

            {/* Data Components - Using Fade for smooth entry */}
            {data.challans && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
                    {/* Challan Overview */}
                    <DeliveryOverview data={data.challans} />

                    {/* Trend + Performance side by side */}
                    <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                        <div style={{ flex: '2 1 420px', minWidth: 320 }}>
                            <DeliveryTrendChart data={data.challans} />
                        </div>
                        <div style={{ flex: '1 1 280px', minWidth: 260 }}>
                            {data.performance && <DeliveryPerformance data={data.performance} />}
                        </div>
                    </div>

                    {/* Personnel - full width */}
                    {data.personnel && <DeliveryPersonnel data={data.personnel} />}
                </div>
            )}
        </Container>
    );
};

export default DeliveryAnalytics;
